// @ts-strict-ignore
import type { AsyncThunk } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { INDEX_REFERENCE, PAGE_INDEX, PARENT_INDEX } from '~/dashboard/views/sars/form-view/constants/form.constant';
import type { SarState } from '~/dashboard/views/sars/form-view/redux/state';
import {
  initialDeletedPages,
  initialMetadataShape,
  initialSarMetadata,
  initialState,
} from '~/dashboard/views/sars/form-view/redux/state';
import type {
  DomainFilters, DomainToken, DomainTokenDraft, DomainTokenReportType,
  ReportWork, SarActivityInstitution, SarEntity, SarFilingInstitution, SarMetadatum,
} from '~/dashboard/views/sars/form-view/types';
import { sarWorkType } from '~/dashboard/views/sars/form-view/types';
import { typhonAjaxFetch } from '~/helpers/api';
import log from '~/helpers/logging';
import { typhonRoutes } from '~/helpers/routes';
import {
  augmentMetadata,
  incomingCompatibilinator,
  incomingMetadataCompatibilinator,
  outgoingCompatibilinator,
  outgoingMetadataCompatibilinator,
  restoreDeletedPagesForAPI,
  TRANSFORMATION_MAP_FROM_API,
  TRANSFORMATION_MAP_FROM_PREFILL,
  TRANSFORMATION_MAP_TO_API,
  transformReport,
  validISOStringOrNull,
} from './helpers';
import type { UserContextValue } from '~/dashboard/context';


const updateValueAtPath = ({
  instanceOfCurrentPage,
  lookupObject,
  parentSectionIndex,
  path,
  sectionIndex,
  value,
}: {
  instanceOfCurrentPage: number;
  lookupObject: object;
  parentSectionIndex?: number;
  path: Array<string>;
  sectionIndex: number;
  value: any;
}) => {
  let key = path[0];
  if (key === INDEX_REFERENCE) {
    key = `${sectionIndex}`;
  } else if (key === PAGE_INDEX) {
    key = `${instanceOfCurrentPage}`;
  } else if (key === PARENT_INDEX) {
    key = `${parentSectionIndex}`;
  }
  const valueAtPath = lookupObject[key];
  if (path[1]) {
    updateValueAtPath({
      instanceOfCurrentPage,
      lookupObject: valueAtPath,
      parentSectionIndex,
      path: path.slice(1),
      sectionIndex,
      value,
    });
  } else {
    lookupObject[key] = value;
  }
};

export const getPrefillAsync = createAsyncThunk(
  'prefill/getPrefill',
  async (
    {
      domainToken,
      userContext,
      caseToken,
      investigationToken,
    }:
    {
      domainToken: DomainToken;
      userContext: Partial<UserContextValue>;
      caseToken?: string;
      investigationToken?: string;
    },
  ) => {
    const { domain, token } = domainToken;
    const url = `${domain}${typhonRoutes.sars.prefill(token)}`;
    const response = await typhonAjaxFetch(url);
    const transformedResponse = incomingCompatibilinator(response);
    const transformedReport = transformReport(
      transformedResponse,
      TRANSFORMATION_MAP_FROM_PREFILL(userContext),
      transformedResponse,
    );
    const compatibleMetadata = incomingMetadataCompatibilinator(initialSarMetadata);
    const transformedMetadata = augmentMetadata(
      initialSarMetadata,
      compatibleMetadata,
      transformedReport,
    );
    return {
      ...transformedReport,
      case_token: caseToken,
      investigation_token: investigationToken,
      metadata: transformedMetadata,
    };
  },
);

export const getReportAsync = createAsyncThunk(
  'report/getReport',
  async ({
    domain, filters, meta, token, version, userContext,
  }: DomainFilters,
  { rejectWithValue }) => {
    let response;
    try {
      if (filters?.length) {
        const url = `${domain}${typhonRoutes.sars.reports({ q: filters, page: 1, per_page: 1 })}`;
        // eslint-disable-next-line no-underscore-dangle
        response = (await typhonAjaxFetch(url))?._embedded?.reports?.[0];
      } else {
        const url = `${domain}${typhonRoutes.sars.report(token, version ? { version } : undefined)}`;
        response = await typhonAjaxFetch(url);
      }
      if (!response) {
        return rejectWithValue({ message: 'Unable to find the requested resource' });
      }
      const transformedResponse = incomingCompatibilinator(response);
      const {
        metadata,
        ...transformedReport
      } = transformReport(
        transformedResponse,
        TRANSFORMATION_MAP_FROM_API(userContext),
        transformedResponse);
      const compatibleMetadata = incomingMetadataCompatibilinator(metadata);
      const transformedMetadata = augmentMetadata(
        initialSarMetadata,
        compatibleMetadata,
        transformedReport,
      );
      return { ...transformedReport, metadata: transformedMetadata };
    } catch (error) {
      const fallbackError = { message: 'Something went wrong fetching the report.' };
      const rejectedValue = rejectWithValue(error.responseError?.message
        ? error.responseError
        : fallbackError);

      log(
        'error',
        'Error fetching SAR report',
        {
          agentEmail: meta.agentEmail,
          isEmployee: meta.isEmployee,
          isAgent: meta.isAgent,
        },
        error.responseError,
      );
      return rejectedValue;
    }
  });

export const postReportWork = createAsyncThunk(
  'report/postReportWork',
  async ({
    domainToken,
    reportWork,
    refetchReport = true,
    userContext,
  }: {
    domainToken: DomainToken;
    reportWork: ReportWork;
    refetchReport?: boolean;
    userContext: Partial<UserContextValue>;
  }, { dispatch, rejectWithValue },
  ) => {
    try {
      const { domain, token } = domainToken;
      const {
        assignedAgentId,
        assignedAgentToken,
        note,
        notificationDays,
        workType,
      } = reportWork;

      const typhonUrl = `${domain}${typhonRoutes.sars.work(token)}`;
      const postBody = {
        assigned_agent_id: assignedAgentId,
        assigned_agent_token: assignedAgentToken,
        note,
        notification_days: notificationDays,
        work_type: workType,
      };

      await typhonAjaxFetch(typhonUrl, {
        method: 'POST',
        body: JSON.stringify(postBody),
      });
      if (refetchReport) {
        await dispatch(getReportAsync({
          domain, token, meta: {}, filters: [], userContext,
        }));
      }
      return {};
    } catch (error) {
      const errorMessage = error.responseError?.message || 'Failed to Request Review';
      return rejectWithValue(errorMessage);
    }
  },
);

export const sendSarReportAsync = createAsyncThunk(
  'report/sendReport',
  async (
    {
      domainToken, isDraft, submitForm, meta, userContext,
    }: DomainTokenDraft,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      await submitForm(); // The form's onsubmit saves the form data to the Redux store.
      const { domain, token } = domainToken;
      const { sars: { deletedPages, metadata, report } } = getState() as { sars: SarState };
      const reportCopy = { ...report };
      const metadataCopy = { ...metadata };
      restoreDeletedPagesForAPI(reportCopy, metadataCopy, deletedPages);
      const reportBody = outgoingCompatibilinator(
        transformReport(reportCopy, TRANSFORMATION_MAP_TO_API, reportCopy),
      );
      const compatibleMetadata = outgoingMetadataCompatibilinator(metadataCopy);
      // we're renaming `sar_activity_institution.branch_activity` to `sar_activity_institution.no_branch_activity`,
      // but we need to keep both values in until the API is updated to handle the new key
      // this new mapping is only temporary hardcoded until we can fully rename the key
      const branchActivity = reportBody?.sar_activity_institution?.branch_activity;
      if (reportBody.sar_activity_institution) {
        reportBody.sar_activity_institution.no_branch_activity = branchActivity || null;
      }
      const options = {
        method: token ? 'PUT' : 'POST',
        body: JSON.stringify({ ...reportBody, metadata: compatibleMetadata, is_draft: isDraft }),
      };
      const url = `${domain}${token ? typhonRoutes.sars.report(token) : typhonRoutes.sars.newReport}`;
      const response = await typhonAjaxFetch(url, options);

      const workType = reportBody.is_ninety_day_review
        ? sarWorkType.flag90DayReview
        : sarWorkType.unflag90DayReview;
      await dispatch(postReportWork({
        domainToken: {
          domain,
          token: token || response?.report_token, // we can use the existing token for existing drafts, or the report token from the typhon response for new reports
        },
        reportWork: { workType, notificationDays: reportBody.notification_days },
        refetchReport: false,
        userContext,
      }));
      await dispatch(getReportAsync({
        domain, token: response?.report_token || token, meta, filters: [], userContext,
      }));

      return { ...response, is_draft: isDraft };
    } catch (error) {
      const fallbackError = { message: `Something went wrong ${isDraft ? 'saving' : 'submitting'} the report.` };
      const rejectedValue = rejectWithValue(error.responseError?.message
        ? error.responseError
        : fallbackError);

      log(
        'error',
        'Error sending SAR report',
        {
          agentEmail: meta.agentEmail,
          isEmployee: meta.isEmployee,
          isAgent: meta.isAgent,
        },
        error.responseError,
      );
      // returning a wrapped rejection allows us to handle the server error at the component level
      return rejectedValue;
    }
  },
);

export const getXMLDownload = createAsyncThunk(
  'XMLDownload/getXMLDownload',
  async ({ domain, token, version }: DomainToken, { rejectWithValue }) => {
    try {
      const url = `${domain}${typhonRoutes.sars.report(token, { version: parseInt(version, 10) })}`;
      const options = {
        json: false,
        headers: {
          'Content-Type': 'application/xml',
        },
      };

      const response = await typhonAjaxFetch(url, options);
      return response;
    } catch (error) {
      // rejecting with value allows us to handle the server error at the component level
      return rejectWithValue(error);
    }
  },
);

export const sendNewReportType = createAsyncThunk(
  'report/sendNewReportType',
  async ({
    domainToken, isAmended, isContinuing, isInitial,
  }: DomainTokenReportType, { rejectWithValue }) => {
    const { domain, token } = domainToken;
    const options = { method: 'POST' };
    const query = {
      is_amended: isAmended || false,
      is_continuing: isContinuing || false,
      is_initial: isInitial || false,
    };
    const url = `${domain}${typhonRoutes.sars.newReportType(token, query)}`;

    try {
      const response = await typhonAjaxFetch(url, options) || {};
      return { ...response, latest_report_version: response.sar_report_version };
    } catch (error) {
      const errorMessage = error.responseError?.message || 'Something went wrong with creating a new report.';
      return rejectWithValue(errorMessage);
    }
  },
);

const sarSlice = createSlice({
  name: 'sars',
  initialState,
  extraReducers: builder => [
    [getPrefillAsync, (state, { metadata, ...report }) => {
      state.report = report;
      state.metadata = metadata;
    }],
    [getReportAsync, (state, { metadata, ...report }) => {
      state.report = report;
      state.metadata = metadata;
      state.deletedPages = initialDeletedPages; // deleted pages are sent to the API but will never come back from the API
    }],
    [sendSarReportAsync, (state, value) => {
      state.report.is_draft = value.is_draft;
      if (value.report_token) state.report.sar_report_token = value.report_token;
    }],
    [sendNewReportType, (state, value) => {
      Object.keys(value).forEach(key => {
        state.report[key] = value[key];
      });
    }],
    [getXMLDownload, (state, value) => { state.xml = value; }],
  ].forEach(([asyncThunk, updater]: [AsyncThunk<any, DomainToken, {}>, any]) => {
    builder.addCase(asyncThunk.pending, state => {
      state.status = 'loading';
      state.inFlightRequests += 1;
    }).addCase(asyncThunk.fulfilled, (state, action) => {
      state.inFlightRequests -= 1;
      if (state.inFlightRequests === 0 && state.status !== 'failed') state.status = 'success';
      updater(state, action.payload);
    }).addCase(asyncThunk.rejected, (state, action) => {
      state.inFlightRequests -= 1;
      state.status = 'failed';
      state.error = action.payload;
    });
  }),
  reducers: {
    setValueAtPath: (state, action) => {
      const {
        instanceOfCurrentPage,
        parentSectionIndex,
        path,
        sectionIndex,
        value,
      } = action.payload;
      updateValueAtPath({
        instanceOfCurrentPage,
        lookupObject: state.report,
        parentSectionIndex,
        path,
        sectionIndex,
        value,
      });
    },
    updateUnknownMetadata: (state, action) => {
      const {
        payload: {
          index,
          instanceOfCurrentPage,
          isUnknown,
          page,
          parentSectionIndex,
          question,
        },
      } = action;
      // if page metadata exists for this page, then we must use the updated metadata shape
      if (state.metadata[page]) {
        // if the page metadata is for an additive page, we must add the appropriate index to the path
        if (Array.isArray(state.metadata[page])) {
          if (typeof parentSectionIndex === 'number') {
            state.metadata[page][instanceOfCurrentPage][question][parentSectionIndex][index]
              .is_unknown = isUnknown;
          } else {
            state.metadata[page][instanceOfCurrentPage][question][index].is_unknown = isUnknown;
          }
        // else we ignore the instanceOfCurrentPage
        } else {
          // @ts-expect-error -- Strict mode required for better types here
          state.metadata[page][question][index].is_unknown = isUnknown;
        }
      // if no page metadata exists, use the legacy metadata lookup
      } else {
        state.metadata[question][index].is_unknown = isUnknown;
      }
    },
    updateErrorMetadata: (state, action) => {
      const {
        payload: {
          errorKey,
          index,
          instanceOfCurrentPage,
          page,
          parentSectionIndex,
          question,
          value,
        },
      } = action;
      // if page metadata exists for this page, then we must use the updated metadata shape
      if (state.metadata[page]) {
        // if the page metadata is for an additive page, we must add the appropriate index to the path
        if (Array.isArray(state.metadata[page])) {
          if (typeof parentSectionIndex === 'number') {
            state.metadata[page][instanceOfCurrentPage][question][parentSectionIndex][index]
              .errors[errorKey] = value;
          } else {
            state.metadata[page][instanceOfCurrentPage][question][index].errors[errorKey] = value;
          }
        // else we ignore the instanceOfCurrentPage
        } else {
          // @ts-expect-error -- Strict mode required for better types here
          state.metadata[page][question][index].errors[errorKey] = value;
        }
      // if no page metadata exists, use the legacy metadata lookup
      } else {
        state.metadata[question][index].errors[errorKey] = value;
      }
    },
    addSectionMetadata: (state, action) => {
      const {
        payload: {
          instanceOfCurrentPage,
          page,
          parentSectionIndex,
          question,
        },
      } = action;
      // if page metadata exists for this page, then we must use the updated metadata shape
      if (state.metadata[page]) {
        // if the page metadata is for an additive page, we must add the appropriate index to the path
        if (Array.isArray(state.metadata[page])) {
          if (typeof parentSectionIndex === 'number') {
            state.metadata[page][instanceOfCurrentPage][question][parentSectionIndex].push(
              Array.isArray(
                state.metadata[page][instanceOfCurrentPage][question][parentSectionIndex][0],
              )
                ? [{ ...initialMetadataShape() }]
                : { ...initialMetadataShape() },
            );
          } else {
            state.metadata[page][instanceOfCurrentPage][question].push(
              Array.isArray(state.metadata[page][instanceOfCurrentPage][question][0])
                ? [{ ...initialMetadataShape() }]
                : { ...initialMetadataShape() },
            );
          }
        // else we ignore the instanceOfCurrentPage
        } else {
          if (typeof parentSectionIndex === 'number') {
            // @ts-expect-error -- Strict mode required for better types here
            state.metadata[page][question][parentSectionIndex].push(
              Array.isArray(state.metadata[page][question][parentSectionIndex][0])
                ? [{ ...initialMetadataShape() }]
                : { ...initialMetadataShape() },
            );
          }
          state.metadata[page][question].push(
            // @ts-expect-error -- Strict mode required for better types here
            Array.isArray(state.metadata[page][question][0])
              ? [{ ...initialMetadataShape() }]
              : { ...initialMetadataShape() },
          );
        }
      // if no page metadata exists, use the legacy metadata lookup
      } else {
        const newMetadata = state.metadata[question] as SarMetadatum[];
        newMetadata.push({ ...initialMetadataShape() });
        state.metadata[question] = newMetadata;
      }
    },
    removeSectionMetadata: (state, action) => {
      const {
        payload: {
          instanceOfCurrentPage,
          page,
          parentSectionIndex,
          question,
          sectionIndex,
        },
      } = action;
      // if page metadata exists for this page, then we must use the updated metadata shape
      if (state.metadata[page]) {
        // if the page metadata is for an additive page, we must add the appropriate index to the path
        if (Array.isArray(state.metadata[page])) {
          if (typeof parentSectionIndex === 'number') {
            const metadataCopy = [
              ...state.metadata[page][instanceOfCurrentPage][question][parentSectionIndex],
            ];
            metadataCopy.splice(sectionIndex, 1);
            state
              .metadata[page][instanceOfCurrentPage][question][parentSectionIndex] = metadataCopy;
          } else {
            const metadataCopy = [...state.metadata[page][instanceOfCurrentPage][question]];
            metadataCopy.splice(sectionIndex, 1);
            state.metadata[page][instanceOfCurrentPage][question] = metadataCopy;
          }
        // else we ignore the instanceOfCurrentPage
        } else if (typeof parentSectionIndex === 'number') {
          // @ts-expect-error -- Strict mode required for better types here
          const metadataCopy = [...state.metadata[page][question][parentSectionIndex]];
          metadataCopy.splice(sectionIndex, 1);
          state.metadata[page][question][parentSectionIndex] = metadataCopy;
        } else {
          const metadataCopy = [...state.metadata[page][question]];
          metadataCopy.splice(sectionIndex, 1);
          // @ts-expect-error -- Strict mode required for better types here
          state.metadata[page][question] = metadataCopy;
        }
      // if no page metadata exists, use the legacy metadata lookup
      } else {
        const currentMetadata = state.metadata[question] as SarMetadatum[];
        const metadataCopy = [...currentMetadata];
        metadataCopy.splice(sectionIndex, 1);
        state.metadata[question] = metadataCopy;
      }
    },
    addPageMetadata: (state, action) => {
      const { payload: { page, initialValue } } = action;
      (state.metadata[page] as Array<{ [prop: string]: Array<SarMetadatum> }>).push(initialValue);
    },
    removeReportPage: (state, action) => {
      // removes report data and metadata when deleting an additive page
      const { payload: { page, index } } = action;
      // delete icon should not render for the first subject anyway,
      // but as an extra precaution we'll prevent the first subject's deletion here as well
      if (index !== 0) {
        const pageArrayCopy = [...(state.report[page] as object[])];
        const pageMetadataCopy = [
          ...(state.metadata[page] as Array<{ [prop: string]: Array<SarMetadatum> }>),
        ];
        const [deletedReportData] = pageArrayCopy.splice(index, 1);
        const [deletedMetadata] = pageMetadataCopy.splice(index, 1);
        state.report[page] = pageArrayCopy;
        state.metadata[page] = pageMetadataCopy;
        state.deletedPages.report[page].push({ ...deletedReportData, is_deleted: true });
        state.deletedPages.metadata[page].push(deletedMetadata);
      }
    },
    setInitialReport: (state, action) => {
      // the minimal required data to create a new report:
      state.report.case_token = action.payload.caseToken;
      state.report.is_draft = true;
    },
    setFilingInformation: state => {
      const newSarFilingInstitution: SarFilingInstitution = {
        ...state.report.filing_institution,
        is_filing_institution: true,
      };

      state.report.filing_institution = newSarFilingInstitution;
    },

    setActivityInformation: state => {
      const newSarActivityInstitution: SarActivityInstitution = {
        ...state.report?.sar_activity_institution,
        is_filing_institution: false,
      };

      state.report.sar_activity_institution = newSarActivityInstitution;
    },

    setEntityInformation: (state, action) => {
      const { payload: { instanceOfCurrentPage } } = action;
      const newEntity: SarEntity = {
        ...state.report.entities[instanceOfCurrentPage],
        action_date: validISOStringOrNull(
          state.report.entities[instanceOfCurrentPage].relationship_tins[0].action_date,
        ),
        has_statement: state.report.entities[instanceOfCurrentPage].statement_in_activity !== null
          ? Boolean(state.report.entities[instanceOfCurrentPage].statement_in_activity)
          : null,
        id_type: state.report.entities[instanceOfCurrentPage].sar_entity_ids[0].id_type,
      };

      // if report returns 500 this will not work
      state.report.entities[instanceOfCurrentPage] = newEntity;
    },
    setSuspiciousActivityInformation: (state, action) => {
      state.report.activity_information.selected_transactions = action.payload.selectedTrxIds;
    },
    setSelectedTrxs: (state, action) => {
      state.report.activity_information.selected_transactions = action.payload;
    },
  },
});

export const {
  addPageMetadata,
  addSectionMetadata,
  removeReportPage,
  removeSectionMetadata,
  setInitialReport,
  setFilingInformation,
  setActivityInformation,
  setEntityInformation,
  setSuspiciousActivityInformation,
  setSelectedTrxs,
  setValueAtPath,
  updateErrorMetadata,
  updateUnknownMetadata,
} = sarSlice.actions;

export default sarSlice.reducer;
